import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api from "@/lib/api";
import { currency } from "@/lib/status";
import { GitMerge, CheckCircle2, Users } from "lucide-react";
import { toast } from "sonner";

export default function StaffDuplicates() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState(null);
  const [merging, setMerging] = useState("");

  const load = () => api.get("/duplicates").then(({ data }) => setGroups(data)).catch(() => setGroups([]));
  useEffect(() => { load(); }, []);

  const mergeGroup = async (g) => {
    const [primary, ...rest] = g.requests;
    setMerging(g.key);
    try {
      for (const r of rest) {
        await api.post(`/requests/${primary.id}/merge`, { source_id: r.id });
      }
      toast.success(`Merged ${rest.length} duplicate(s) into one lead`);
      load();
    } catch { toast.error("Merge failed"); } finally { setMerging(""); }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-50">Duplicate queue</h1>
        <p className="mt-1 text-slate-600 dark:text-slate-300">Same-buyer leads for the same vehicle detected across web, mobile, and manual intake. Merge each group into a single lead.</p>

        {groups === null ? (
          <div className="mt-8 space-y-4">{Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-lg" />)}</div>
        ) : groups.length === 0 ? (
          <div className="mt-10 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-12 text-center" data-testid="dup-empty">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-6 w-6" /></div>
            <p className="mt-4 font-display text-xl font-bold text-slate-900 dark:text-slate-100">No duplicates</p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Every active lead is unique. New duplicates from the same buyer auto-merge on intake.</p>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            {groups.map((g) => {
              const first = g.requests[0];
              return (
                <div key={g.key} data-testid="dup-group" className="rounded-xl border border-amber-200 bg-white dark:bg-slate-900 p-6">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-amber-600"><Users className="h-4 w-4" /> {g.requests.length} leads · same buyer</p>
                      <p className="mt-1 font-display text-lg font-bold text-slate-900 dark:text-slate-100">{first.year} {first.make} {first.model}</p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">{first.buyer_name} · {first.buyer_email}</p>
                    </div>
                    <Button onClick={() => mergeGroup(g)} disabled={merging === g.key} data-testid="merge-group-btn" className="rounded-md bg-slate-950 font-semibold hover:bg-slate-800"><GitMerge className="mr-1.5 h-4 w-4" /> {merging === g.key ? "Merging…" : "Merge into one"}</Button>
                  </div>
                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {g.requests.map((r, idx) => (
                      <div key={r.id} onClick={() => navigate(`/staff/requests/${r.id}`)} className="cursor-pointer rounded-lg border border-slate-200 dark:border-slate-800 p-3 hover:border-rose-300">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold uppercase text-slate-400 dark:text-slate-500">{idx === 0 ? "Primary" : "Duplicate"}</span>
                          <span className="text-xs text-slate-400 dark:text-slate-500">{(r.channels || []).join(", ")}</span>
                        </div>
                        <p className="mt-1 text-sm font-semibold capitalize text-slate-800 dark:text-slate-200">{r.status.replace(/_/g, " ")}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{r.quote ? currency(r.quote.total) : r.msrp ? currency(r.msrp) : "No quote"} · updated {new Date(r.updated_at).toLocaleDateString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
